import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BACKEND_BASE_URL } from '../constants/api';
import { useAuthStore } from '../stores/authStore';
import { NOTIFICATION_QUERY_KEYS, showNotificationToast } from '../utils/notificationHelpers';
import useNotificationStore from '../stores/notificationStore';
import { hasNotificationPermission, showBrowserNotification } from '../utils/browserNotifications';

/** SSE requires backend token-in-query support — off by default. */
const SSE_ENABLED =
  process.env.REACT_APP_ENABLE_NOTIFICATION_SSE === 'true' ||
  process.env.REACT_APP_ENABLE_NOTIFICATION_SSE === '1';

/**
 * Optional SSE subscription. Enable when backend supports authenticated stream.
 */
export function useNotificationStream() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const token = useAuthStore((state) => state.token);
  const showNotification = useNotificationStore((state) => state.showNotification);
  const esRef = useRef(null);

  useEffect(() => {
    if (!SSE_ENABLED || !isAuthenticated || !token) {
      return undefined;
    }

    const url = `${BACKEND_BASE_URL}/api/v1/notifications/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    const onNotification = (event) => {
      try {
        const notification = JSON.parse(event.data);
        const id = notification._id || notification.id;
        if (!id) return;

        // Drop seller-only notifications on the buyer site
        if (notification.target === 'seller') return;

        queryClient.setQueryData(NOTIFICATION_QUERY_KEYS.list, (old = []) => {
          if (old.some((n) => (n._id || n.id) === id)) return old;
          return [notification, ...old];
        });
        queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.unreadCount });
        showNotificationToast(notification, showNotification);

        if (hasNotificationPermission() && !document.hasFocus()) {
          showBrowserNotification(notification.title, {
            body: notification.body || notification.message,
            tag: `notification-${id}`,
            data: notification.data,
          });
        }
      } catch (e) {
        console.warn('SSE notification parse error', e);
      }
    };

    es.addEventListener('notification', onNotification);
    es.onerror = () => {
      es.close();
      esRef.current = null;
    };

    return () => {
      es.removeEventListener('notification', onNotification);
      es.close();
      esRef.current = null;
    };
  }, [isAuthenticated, token, queryClient, showNotification]);
}

export default useNotificationStream;
